'use client';

import { ReactNode, useCallback, useState } from 'react';
import { isThisMonth, isPast, format } from 'date-fns';
import {
  DayPicker,
  DayPickerProps,
  Locale as DayPickerLocale,
} from 'react-day-picker';
import { enGB, de, fr, arSA } from 'react-day-picker/locale';
import { css, Z_INDEX_LIBRARY } from '@bts-web/utils-style-engine';
import { isRtlLanguageFromLocale, Locale } from '@bts-web/utils-lokalise';
import { RegularChevronLeft, RegularChevronRight } from '../../svgIcons';

type DatePickerProps = {
  sideOffset?: number;
  zIndex?: number;
  cancelLabel: string;
  disabled?: DayPickerProps['disabled'];
  confirmLabel: string;
  contentStyle?: React.CSSProperties;
  value: string | undefined;
  disablePast?: boolean;
  locale: Locale;
  onChange: (date: string) => void;
  children: ReactNode;
};

const datePickerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '358px',
  maxWidth: '100%',
  backgroundColor: 'neutrals.card_fill_primary',
  borderEndEndRadius: 'smallBottomRight',
  borderEndStartRadius: 'smallTopRight',
  borderStartStartRadius: 'smallTopLeft',
  borderStartEndRadius: 'smallBottomLeft',
  p: 'small',
  '& [data-element="date-picker-open-trigger"]': {
    width: '100%',
  },
  '& [data-element="date-picker-caption"]': {
    fontSize: 'title.large',
    fontWeight: 'title.large',
    lineHeight: 'title.large',
    letterSpacing: 'title.large',
    color: 'neutrals.text_primary',
    height: '44px',
    px: 'extra_small_2',
    display: 'flex',
    alignItems: 'center',
  },

  '& [data-element="date-picker-nav"]': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'absolute',
    insetInlineEnd: 'small',
    boxSizing: 'border-box',
    top: 'small',
    '& button': {
      color: 'information.fill_primary',
      height: '44px',
      width: '24px',
    },
    '& > button:disabled': {
      color: 'neutrals.fill_tertiary',
    },
  },
  '& [data-element="date-picker-footer"]': {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 'extra_small',
    '& button': {
      color: 'information.fill_primary',
      height: '32px',
    },
    '& > button:first-child': {
      fontSize: 'label.medium_medium',
      lineHeight: 'label.medium_medium',
      letterSpacing: 'label.medium_medium',
      fontWeight: 'label.medium_medium',
    },
    '& > button:not(:disabled):last-child': {
      fontSize: 'label.large_bold',
      fontWeight: 'label.large_bold',
      lineHeight: 'label.large_bold',
      letterSpacing: 'label.large_bold',
    },
    '& > button:disabled': {
      color: 'neutrals.fill_tertiary',
    },
  },
  '& table': {
    width: '100%',
    textIndent: 0,
    borderColor: 'inherit',
    borderCollapse: 'collapse',
  },
  '& th': {
    color: 'neutrals.fill_tertiary',
    fontSize: 'body.medium',
    fontWeight: 'body.medium',
    letterSpacing: 'body.medium',
    lineHeight: 'body.medium',
    width: '40px',
    textAlign: 'center',
  },
  '& tr': {
    display: 'flex',
    justifyContent: 'space-between',
  },
  '& td': {
    color: 'neutrals.text_primary',
    height: '40px',
    width: '40px',
  },
  '& td button': {
    height: '40px',
    width: '40px',
    fontSize: 'headline.small',
    fontWeight: 'headline.small',
    lineHeight: 'headline.small',
    letterSpacing: 'headline.small',
    '&:disabled': {
      opacity: 0.4,
    },
  },
  '& td[data-today="true"] button': {
    color: 'information.fill_primary',
  },
  '& td[data-outside="true"] button': {
    visibility: 'hidden',
    opacity: '0',
  },
  '& td[data-selected="true"]': {
    borderRadius: '22px',
    backgroundColor: 'information.fill_focused',
    '& > button': {
      color: 'information.fill_primary',
      fontSize: 'headline.small_medium',
      fontWeight: 'headline.small_medium',
      lineHeight: 'headline.small_medium',
      letterSpacing: 'headline.small_medium',
    },
  },
});

const dayPickerLocaleByAppLocale: Record<Locale, DayPickerLocale> = {
  'en-GB': enGB,
  'de-DE': de,
  'fr-FR': fr,
  'ar-AR': arSA,
};

const DatePicker = ({
  cancelLabel,
  confirmLabel,
  contentStyle,
  children,
  disabled,
  onChange,
  zIndex = Z_INDEX_LIBRARY.MODAL,
  value = format(new Date(), 'yyyy-MM-dd'),
  disablePast = true,
  locale,
}: DatePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleConfirm = () => {
    if (selectedDate) {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');

      onChange(formattedDate);
    }

    setSelectedDate(undefined);

    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedDate(undefined);

    setIsOpen(false);
  };

  const isPastDate = useCallback(
    (date: Date | number) => disablePast && isPast(date),
    [disablePast],
  );

  const dateValue = new Date(value);

  const isRTL = isRtlLanguageFromLocale(locale);

  return (
    <>
      <button
        type="button"
        data-element="date-picker-open-trigger"
        onClick={() => setIsOpen(true)}
      >
        {children}
      </button>

      {isOpen && (
        <>
          <div
            className={css({
              position: 'fixed',
              inset: '0',
              backgroundColor: 'rgba(166, 166, 166, 0.50)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              width: '100vw',
              overflow: 'hidden',
            })}
            data-testid="date-picker-overlay"
            style={{ zIndex }}
            onClick={handleCancel}
          />

          <div
            style={{ zIndex, ...contentStyle }}
            data-testid="date-picker-content"
            className={css({
              position: 'absolute',
              bottom: 'large',
              width: '358px',
              maxWidth: '100%',
              insetInline: 0,
              insetInlineEnd: 0,
              margin: '0 auto',
              '&[data-side="top"]': {
                animationName: 'slideUpAndFade',
              },
              '&[data-side="bottom"]': {
                animationName: 'slideUpAndFade',
              },
            })}
            onClick={(e) => e.stopPropagation()}
          >
            <DayPicker
              mode="single"
              className={datePickerStyles}
              selected={selectedDate || dateValue}
              disabled={disabled}
              onSelect={setSelectedDate}
              footer
              required
              dir={isRTL ? 'rtl' : 'ltr'}
              locale={dayPickerLocaleByAppLocale[locale]}
              defaultMonth={dateValue}
              components={{
                CaptionLabel: ({ children }) => {
                  return (
                    <div
                      data-element="date-picker-caption"
                      data-testid="date-picker-caption"
                    >
                      {children}
                    </div>
                  );
                },
                Nav: ({ onNextClick, previousMonth, onPreviousClick }) => {
                  const now = new Date();

                  const earliestDate = previousMonth
                    ? new Date(
                        Math.min(now.getTime(), previousMonth?.getTime()),
                      )
                    : now;

                  const canPrev = earliestDate
                    ? !isPastDate(earliestDate) || isThisMonth(earliestDate)
                    : false;

                  return (
                    <div
                      data-element="date-picker-nav"
                      data-testid="date-picker-nav"
                      className={css({
                        flexDirection: isRTL ? 'row-reverse' : 'row',
                      })}
                    >
                      {onPreviousClick && (
                        <button
                          type="button"
                          onClick={onPreviousClick}
                          disabled={!canPrev}
                          tabIndex={1}
                        >
                          <RegularChevronLeft size={24} />
                        </button>
                      )}

                      {onNextClick && (
                        <button type="button" onClick={onNextClick}>
                          <RegularChevronRight size={24} />
                        </button>
                      )}
                    </div>
                  );
                },
                Footer: () => {
                  return (
                    <div data-element="date-picker-footer">
                      <button
                        type="button"
                        onClick={handleCancel}
                        data-testid="date-picker-cancel"
                      >
                        {cancelLabel}
                      </button>

                      <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!selectedDate}
                        data-testid="date-picker-confirm"
                      >
                        {confirmLabel}
                      </button>
                    </div>
                  );
                },
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export { DatePicker };
